import { QuestelServicesConstants } from '@/models/QuestelServicesConstants';

export default class Common {

    /**
     * Returns TRUE if the specified value has valid value (not undefined, not null, not blank string).
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is not missing, null, or blank.
     */
    public static hasValue(value: any): boolean {
        return (typeof (value) !== 'undefined' && value !== null && value !== '');
    }

    /**
     * Returns TRUE if the specified value is missing or null (undefined or null).
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is missing or null.
     */
    public static isNull(value: any): boolean {
        return (typeof (value) === 'undefined' || value === null);
    }

    /**
     * Returns TRUE if the specified value is an object.
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is an object.
     */
    public static isObject(value: any): boolean {
        return (typeof (value) === 'object' && value !== null);
    }

    /**
     * Returns TRUE if the specified value is a function.
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is a function.
     */
    public static isFunction(value: any): boolean {
        return (typeof (value) === 'function' && value !== null);
    }

    /**
     * Returns TRUE if the specified value is a string.
     *
     * @param {any} value The value to test.
     * @param {boolean} checkIfStringIsBlank If TRUE, the value will also be checked for a blank value.
     * @returns {boolean} Returns TRUE if the value is a string.
     */
    public static isString(value: any, checkIfStringIsBlank: boolean = false): boolean {
        if (checkIfStringIsBlank === true) {
            return (typeof (value) === 'string' && value !== null && value !== '');
        }
        return (typeof (value) === 'string' && value !== null);
    }

    /**
     * Returns TRUE if the specified value is a string.
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is a string.
     */
    public static isBlankString(value: any): boolean {
        return (typeof (value) === 'string' && value !== null && value === '');
    }

    /**
     * Returns TRUE if the specified value is a boolean (true/false).
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is a boolean (true/false).
     */
    public static isBoolean(value: any): boolean {
        return (typeof (value) === 'boolean' && value !== null);
    }

    /**
     * Returns TRUE if the specified value is a number, and is not equal to NaN.
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is a number, and is not equal to NaN.
     */
    public static isNumber(value: any): boolean {
        return (typeof (value) === 'number' || typeof (value) === 'string' && value.trim() !== '') && !isNaN(value as number);
    }

    /**
     * Returns TRUE if the specified value is an array.
     *
     * @param {any} value The value to test.
     * @param {boolean} [checkIfArrayIsEmpty] OPTIONAL - If TRUE, the array must be valid and have at least 1 item within it. If FALSE (default), the array must only be valid (items within it do not matter).
     * @returns {boolean} Returns TRUE if the value is an array.
     */
    public static isArray(value: any, checkIfArrayIsEmpty: boolean = false): boolean {
      if (typeof (value) !== 'undefined' && value !== null && Object.prototype.toString.call(value) === '[object Array]') {
        return checkIfArrayIsEmpty ? (value.length > 0) : true;
      } else {
        return false;
      }
    }

    /**
     * Returns TRUE if the specified value is a date.
     *
     * @param {any} value The value to test.
     * @returns {boolean} Returns TRUE if the value is a date.
     */
    public static isDate(value: any): boolean {
        return (typeof (value) !== 'undefined' && value !== null && Object.prototype.toString.call(value) === '[object Date]');
    }

    /**
     * Tests the specified string value to see if is a GUID value.
     *
     * @param {string} value The value to test.
     */
    public static isGuid(value: string): boolean {
        var regexGuid: RegExp = /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/gi;
        return regexGuid.test(value);
    }

    /**
     * Converts the specified value to a string.
     *
     * @param {any} value The value to convert.
     */
    public static toString(value: any): string {
        if (Common.isNull(value)) {
            return '';
        } else if (typeof (value) === 'string') {
            return value;
        } else if (typeof (value) === 'boolean') {
            return value ? 'true' : 'false';
        } else {
            return value.toString();
        }
    }

    /**
     * Converts the specified value to an integer.
     *
     * @param {any} value The value to convert.
     */
    public static toInteger(value: any): number {
        if (!Common.isNumber(value)) {
            return 0;
        } else {
            return parseInt(value.toString(), 10);
        }
    }

    /**
     * Converts the specified value to a floating-point number.
     *
     * @param {any} value The value to convert.
     */
    public static toFloat(value: any): number {
        if (!Common.isNumber(value)) {
            return 0;
        } else {
            return parseFloat(value.toString());
        }
    }

    /**
     * Converts the specified value to a boolean.
     *
     * @param {any} value The value to convert.
     * @param {boolean} defaultValue The value to use if the specified 'value' parameter is missing (NULL or UNDEFINED).
     */
    public static toBoolean(value: any, defaultValue: boolean = false): boolean {
        if (Common.isNull(value)) {
            return defaultValue;
        } else if (Common.isString(value)) {
            if (value.toLowerCase() === 'true' || value === '1') {
                return true;
            } else {
                return false;
            }
        } else {
            return value ? true : false;
        }
    }

    /**
     * Converts the specified value to a Date.
     *
     * @param {any} value The value to convert.
     */
    public static toDate(value: any): Date | null {
        try {
            if (Common.isString(value, true) || Common.isNumber(value) || Common.isDate(value)) {
                return new Date(value);
            }
            if (Common.isObject(value)) {
                return new Date(value.year, value.month, value.date, value.hours, value.minutes, value.seconds, value.milliseconds);
            }
            return null;
        } catch (ex) {
            return null;
        }
    }

    /**
     * Gets the HTTP response status text from the specified status code.
     *
     * @param {number} code The status code.
     */
    public static getStatusCodeText(code: number): string {

        if (Common.isNull(code)) {
          return '';
        }

        switch (code) {

            case 0:   return `(${code}) Unknown / Possible Network Connection Problem`;

            case 100: return `(${code}) Continue`;
            case 101: return `(${code}) Switching Protocols`;
            case 102: return `(${code}) Processing`;

            case 200: return `(${code}) OK`;
            case 201: return `(${code}) Created`;
            case 202: return `(${code}) Accepted`;
            case 203: return `(${code}) Non-Authoritative Information`;
            case 204: return `(${code}) No Content`;
            case 205: return `(${code}) Reset Content`;
            case 206: return `(${code}) Partial Content`;
            case 207: return `(${code}) Multi-Status`;
            case 208: return `(${code}) Already Reported`;
            case 226: return `(${code}) IM Used`;

            case 300: return `(${code}) Multiple Choices`;
            case 301: return `(${code}) Moved Permanently`;
            case 302: return `(${code}) Found (Response.Redirect)`;
            case 303: return `(${code}) See Other`;
            case 304: return `(${code}) Not Modified`;
            case 305: return `(${code}) Use Proxy`;
            case 306: return `(${code}) Switch Proxy`;
            case 307: return `(${code}) Temporary Redirect`;
            case 308: return `(${code}) Permanent Redirect`;

            case 400: return `(${code}) Bad Request`;
            case 401: return `(${code}) Unauthorized`;
            case 402: return `(${code}) Payment Required`;
            case 403: return `(${code}) Forbidden`;
            case 404: return `(${code}) Not Found`;
            case 405: return `(${code}) Method Not Allowed`;
            case 406: return `(${code}) Not Acceptable`;
            case 407: return `(${code}) Proxy Authentication Required`;
            case 408: return `(${code}) Request Timeout (client took too long to send)`;
            case 409: return `(${code}) Conflict`;
            case 410: return `(${code}) Gone`;
            case 411: return `(${code}) Length Required`;
            case 412: return `(${code}) Precondition Failed`;
            case 413: return `(${code}) Payload Too Large`;
            case 414: return `(${code}) URI Too Long`;
            case 415: return `(${code}) Unsupported Media Type`;
            case 416: return `(${code}) Range Not Satisfiable`;
            case 417: return `(${code}) Expectation Failed`;
            case 418: return `(${code}) I\`m a teapot (IETF April Fools\` joke)`;
            case 421: return `(${code}) Misdirected Request`;
            case 422: return `(${code}) Unprocessable Entity`;
            case 423: return `(${code}) Locked`;
            case 424: return `(${code}) Failed Dependency`;
            case 426: return `(${code}) Upgrade Required`;
            case 428: return `(${code}) Precondition Required`;
            case 429: return `(${code}) Too Many Requests`;
            case 431: return `(${code}) Request Header Fields Too Large`;
            case 451: return `(${code}) Unavailable For Legal Reasons`;

            case 500: return `(${code}) Internal Server Error`;
            case 501: return `(${code}) Not Implemented`;
            case 502: return `(${code}) Bad Gateway`;
            case 503: return `(${code}) Service Unavailable`;
            case 504: return `(${code}) Gateway Timeout (server timed out waiting for another service [i.e. - Kestrel for .NET Core])`;
            case 505: return `(${code}) HTTP Version Not Supported`;
            case 506: return `(${code}) Variant Also Negotiates`;
            case 507: return `(${code}) Insufficient Storage`;
            case 508: return `(${code}) Loop Detected`;
            case 510: return `(${code}) Not Extended`;
            case 511: return `(${code}) Network Authentication Required`;

            default:
                let firstNumber: string = code.toString().substr(0, 1);
                switch (firstNumber) {
                    case '1': return `(${code}) Informational response`;
                    case '2': return `(${code}) Success`;
                    case '3': return `(${code}) Redirection`;
                    case '4': return `(${code}) Client Error`;
                    case '5': return `(${code}) Server Error`;
                    default:  return `(${code}) Unknown response code`;
                }
        }
    }

    /**
     * Gets the type from the specified object. Works on JurisdictionItemDto and LocaleInfo objects.
     *
     * @param {any} item The object to parse to get the type number from.
     */
    public static getType(item: any): number {
        if (Common.isNull(item)) {
            return -1;
        }
        if (Common.isNumber(item.type)) {
            return item.type;
        }
        if (Common.isNumber(item.value?.type)) {
            return item.value.type;
        }
        return null;
    }

    /**
     * Allows you to call an async function that will "sleep" the current async function by calling "setTimeout" within a Promise.
     *
     * @param {number} milliseconds The number of milliseconds to wait before continuing the execution of the current async function.
     */
    public static async wait(milliseconds: number): Promise<any> {
        return new Promise<any>((resolve: Function, reject: Function) => {
            window.setTimeout(resolve, milliseconds);
        });
    }

    /**
     * Adds a new group of messages/details to the console.
     *
     * @param {string} message The message to display.
     * @param {string} details The details that can be viewed if the group is expanded.
     */
    public static logCollapsedDetails(message: string, details: any): void {
        window.console.groupCollapsed(message);
        window.console.dir(details);
        window.console.groupEnd();
    }

    /**
     * Returns an array of elements that are distinct within the specified "items" array.
     *
     * If the "propertyName" parameter is specified and the "items" parameter contains objects, the "items" parameter is treated as an array of
     * objects, and the "propertyName" parameter is used to check for distinct values within a specific property on each object.
     *
     * If the "propertyName" parameter is NOT specified, or the "items" parameter contains primitive types... the values of each element in the
     * "items" array parameter is checked for uniqueness.
     *
     * @template T The type that is passed into the "items" parameter, and returned by the function. Can be a primitive type, or an object.
     * @param {Array<T>} items A list of objects, or primitive values.
     * @param {string} [propertyName] OPTIONAL - The name of the property on the objects passed into the "items" parameter. Leave NULL (default) if the "items" parameter contains primitive types.
     * @returns {Array<T>} Returns a new array (of the specified type) which contains only the distinct/unique elements found within the "items" array parameter.
     */
    public static getDistinctItemsFromArray<T>(items: Array<T>, propertyName: string = null): Array<T> {

        if (!Common.isArray(items)) {
            return null;
        } else if (items.length < 1) {
            return [];
        }

        let inspectProperty: boolean = Common.hasValue(propertyName) && Common.isObject(items[0]);
        let distinctValues: object = {};
        return items.filter(function (item: T, index: number, array: Array<T>) {
            let propertyValue: any;
            if (inspectProperty) {
                propertyValue = item[propertyName];
            } else {
                propertyValue = item;
            }
            return distinctValues.hasOwnProperty(propertyValue) ? false : (distinctValues[propertyValue] = true);
        });
  }

  /**
   * Attempts to parse the file name out of the "content-disposition" header that is returned during a file download request to the server.
   *
   * @param {Response} response The Response object that is returned from making a "fetch(..)" request to the server to download a file.
   * @param {string} defaultFileName [OPTIONAL] The name of the file to return if it can not be parsed from the response headers.
   * @returns {string} Returns the name of the file from the "content-disposition" header (or the specified default if it can not be found).
   */
  public static getFileNameFromHeaders(response: Response, defaultFileName: string = 'File'): string {

    let fileName: string = defaultFileName;

    if (!Common.isNull(response?.headers)) {

      let contentDisposition: string = '';

      response.headers.forEach((value: string, key: string, parent: Headers) => {
        if (key === 'content-disposition') {
          contentDisposition = value;
        }
      });

      if (Common.hasValue(contentDisposition)) {

        let utf8FilenameRegex: RegExp = /filename\*=UTF-8''([\w%\-\.]+)(?:; ?|$)/i;
        let asciiFilenameRegex: RegExp = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

        if (utf8FilenameRegex.test(contentDisposition)) {
          fileName = decodeURIComponent(utf8FilenameRegex.exec(contentDisposition)[1]);
        } else {
          const matches: RegExpExecArray = asciiFilenameRegex.exec(contentDisposition);
          if (matches != null && matches[2]) {
            fileName = matches[2];
          }
        }
      }
    }

    return fileName;
  }

  /**
   * Takes a blob URL that resulted from a file download, and pops up a download dialog for the file to start downloading.
   * This is done by adding an "A" (anchor) tag to the DOM, then automating a CLICK event on it (as if the user clicked on a "download" hyperlink on the page).
   *
   * @param {string} blobUrl The URL created by calling "window.URL.createObjectURL(blob)" with a blob object that is returned from a file download request.
   * @param {string} fileName The name of the file to download.
   */
  public static startDownload(blobUrl: string, fileName: string): void {
    let a: HTMLAnchorElement = document.createElement('a');
    a.download = fileName;
    a.href = blobUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  /**
   * Handles the 'keydown' and 'keyup' events of a field that should only allow numbers to be entered in it.
   * This is needed since using just the attribute 'type="number"' will still allow user to enter e (which stands for exponential).
   * NOTE: This only allows numbers 0 - 9. It does not allow decimals (.), or commas (,), or negative symbols (-), or any other symbol or letter.
   * NOTE: The 'keypress' event should NOT be used because it is deprecated (https://developer.mozilla.org/en-US/docs/Web/API/Document/keypress_event).
   *
   * @param {KeyboardEvent} e An object containing information about the keyboard event.
   * @returns {boolean} Returns TRUE if the key is 0 - 9 and should be allowed.  Returns FALSE otherwise.
   */
  public static keyMustBeNumber(e: KeyboardEvent): boolean {

    const allowedCharacters: string = "1234567890";
    const allowedKeys: Array<string> = ["Backspace", "Tab", "Delete", "Control"];
    const allowedLetters: Array<string> = ["KeyA", "KeyC", "KeyV", "KeyX"];

    // Validate parameters
    if (Common.isNull(e)) {
      return false;
    }

    // If it was a valid character OR any of the allowed keys OR CTRL/⌘ + allowed letters, return TRUE
    if (allowedCharacters.indexOf(e.key) > -1 || allowedKeys.indexOf(e.key) > -1 || ((e.ctrlKey || e.metaKey) && allowedLetters.indexOf(e.code) > -1)) {
      return true;
    }

    // If it was an invalid character, prevent propagation of the keyboard event, and return FALSE
    e.preventDefault();
    return false;
  }
}
